/** @jsx jsx */
import { jsx, } from '@emotion/core';
import { ClassAttributes, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import slugify from 'slugify';

import {
  Page,
  Header,
  PageContent,
  VehicleInfo,
  Footer,
  FlexContainer,
  FlexRow,
  FlexCell,
  ResizablePageContent,
} from '../../sew-common';
import {
  Vehicle,
  ConsumerReviewAndRating,
  ExpertReviewAndRating,
  VehicleAward,
  AccountInfoData,
} from '../../sew-core/types';
import { Panels, } from './Constants';
import GetErrorPanel from './ErrorPanel';
import ValidPanel from '../containers/ValidPanel';

const StickyHeaderContainer = styled.div`
  width: 100%;
  z-index: 100;
`;

const fixedHeightStyle = {
  height: '580px',
};

const resizableHeightStyle = {
  height: 'auto',
};

interface SewWidgetProps extends ClassAttributes<any> {
  vehicle: Vehicle;
  consumerReviewAndRating: ConsumerReviewAndRating;
  expertReviewAndRating: ExpertReviewAndRating;
  vehicleAward: VehicleAward;
  accountInfoData: AccountInfoData;
  queryParams?: any;
  errors?: any;
  initialRender?: boolean;
  dataReceived: (panelsWithData: string) => void;
}

const SewWidget = ({
  vehicle,
  consumerReviewAndRating,
  expertReviewAndRating,
  vehicleAward,
  accountInfoData,
  errors,
  queryParams,
  initialRender,
  dataReceived,
}: SewWidgetProps): JSX.Element => {
  const { consumerRating, consumerReviews, numberOfReviews, } = consumerReviewAndRating;
  const { editorOverview, pros, cons, expertRating, } = expertReviewAndRating;
  const { panels, debug, } = queryParams;
  const shouldEnableKBBLink: boolean = queryParams.shouldenablekbblink === 'true';
  const contentArea = useRef<HTMLDivElement>(null);

  let fixedheight: boolean = true;
  if(queryParams.fixedheight && queryParams.fixedheight.toLowerCase() === 'false'){
    fixedheight = false;
  }

  // Cleanse panels for incorrect text and duplicates
  const validPanels = [Panels.EXPERT_REVIEWS, Panels.EXPERT_RATINGS, Panels.CONSUMER_REVIEWS, Panels.CONSUMER_RATINGS, Panels.AWARDS];
  let requestedPanels: string[] = [];
  if (panels) {
    requestedPanels = panels.split(',');
    requestedPanels = requestedPanels.filter((value: any) => validPanels.includes(value));
    requestedPanels = [...new Set(requestedPanels)];
  }

  useEffect(() => {
    if(!fixedheight){
      window.parent.postMessage({
        eventSource: "EmbeddedWidget",
        eventAction: "heightChanged",
        height: contentArea.current?.clientHeight,
      }, '*');
    }

  })

  // event handler to get page height and postmessage to parent page
  // this will be invoked on every page resizing event that happens in the component.
  if (contentArea.current){
    let accordionClicksToJobIntervals = new Map<string, NodeJS.Timeout>();
    let expansionTime = 10;

    if(!fixedheight){
      window.parent.postMessage({
        eventSource: "EmbeddedWidget",
        eventAction: "heightChanged",
        height: contentArea.current?.clientHeight,
      }, '*');
    }

    // When accordion animation starts
    contentArea.current.addEventListener('transitionstart', (event) => {
      let accordionDiv = event.target as HTMLDivElement;
      let accordionClassName = accordionDiv.className.toString();

      if(accordionDiv.id.includes("Accordion")){
        let sendPostMessageFunction = () => {
          if(accordionClicksToJobIntervals.has(accordionDiv.id)){
            window.parent.postMessage({
              eventSource: "EmbeddedWidget",
              eventAction: "heightChanged",
              height: contentArea.current?.clientHeight,
            }, '*');
          }
        }
        let interval = setInterval(sendPostMessageFunction, expansionTime);

        if(accordionClicksToJobIntervals.has(accordionDiv.id)){
          let previousJobInterval = accordionClicksToJobIntervals.get(accordionDiv.id);
          if(previousJobInterval){
            clearInterval(previousJobInterval);
          }
          
        }

        // Kill off old process and readd new process if it exists. This is when the same accordion is clicked twice or more
        accordionClicksToJobIntervals.set(accordionDiv.id, interval);
      }
      else if (accordionClassName.includes('ExpanderContent')){
        let sendPostMessageFunction = () => {
          if(accordionClicksToJobIntervals.has(accordionClassName)){
            window.parent.postMessage({
              eventSource: "EmbeddedWidget",
              eventAction: "heightChanged",
              height: contentArea.current?.clientHeight,
            }, '*');
          }
        }
        let interval = setInterval(sendPostMessageFunction, expansionTime);

        if(accordionClicksToJobIntervals.has(accordionClassName)){
          let previousJobInterval = accordionClicksToJobIntervals.get(accordionDiv.id);
          if(previousJobInterval){
            clearInterval(previousJobInterval);
          }
          
        }

        // Kill off old process and readd new process if it exists. This is when the same accordion is clicked twice or more
        accordionClicksToJobIntervals.set(accordionClassName, interval);        
      }
    });

    // When accordion animation ends
    contentArea.current.addEventListener('transitionend', (event) => {
      let accordionDiv = event.target as HTMLDivElement;
      let accordionClassName = accordionDiv.className.toString();
      if(accordionDiv.id.includes("Accordion")){
        let expansionIntervalJob = accordionClicksToJobIntervals.get(accordionDiv.id);
        if(expansionIntervalJob){
          clearInterval(expansionIntervalJob);
        }
        accordionClicksToJobIntervals.delete(accordionDiv.id);
      }
      else if(accordionClassName.includes('ExpanderContent')){
        let expansionIntervalJob = accordionClicksToJobIntervals.get(accordionClassName);
        if(expansionIntervalJob){
          clearInterval(expansionIntervalJob);
        }
        accordionClicksToJobIntervals.delete(accordionClassName);        
      }
    });
  }

  // Default do not show panels if initial render or if empty array
  let panelsToDisplay: any = '';
  let readMoreLink = '';
  let privacyPolicyLink = 'https://www.kbb.com/company/privacy-policy/';
  let isOptedOutOfEWBacklink = false;

  if (initialRender === false) {
    if (debug === 'true') {
      // eslint-disable-next-line no-console
      console.log('Syndicated Editorial Widget Data:', {
        ExpertReviews: { editorOverview, pros, cons, },
        ExpertRatings: expertRating,
        ConsumerReviews: consumerReviews,
        ConsumerRatings: consumerRating,
        Awards: vehicleAward,
      });
      // eslint-disable-next-line no-console
      console.log('Syndicated Editorial Widget Errors:', errors);
    }

    // Create Read More Button URL
    if (shouldEnableKBBLink) {
      if (!vehicle?.makeName || !vehicle.modelName) {
        readMoreLink = '';
      } else if (vehicle?.yearId !== 0) {
        readMoreLink = `https://www.kbb.com/${slugify(vehicle.makeName).toLowerCase()}/${slugify(vehicle.modelName).toLowerCase()}/${vehicle.yearId}/`;
      } else {
        readMoreLink = `https://www.kbb.com/${slugify(vehicle.makeName).toLowerCase()}/${slugify(vehicle.modelName).toLowerCase()}/`;
      }
    } 
    
    // Create Privacy Policy Button URL
    const { isExcludedFromYmmPrivacyPolicy } = accountInfoData;
    isOptedOutOfEWBacklink = accountInfoData?.isOptedOutOfEWBacklink ? accountInfoData?.isOptedOutOfEWBacklink : false;
    // only link back to kbb.com YMM page if dealer hasn't opted out to do so
    if (!isExcludedFromYmmPrivacyPolicy && vehicle?.makeName && vehicle?.modelName && vehicle?.yearId) {
      // use slugify to coerce HTML encoding to English equivalent
      // for example: "Ranger Rover" -> "Range%20Rover" in URL -> need to be slugified to "Ranger-Rover" to work in KBB.com resource path routing
      privacyPolicyLink = `https://www.kbb.com/${slugify(vehicle.makeName).toLowerCase()}/${slugify(vehicle.modelName).toLowerCase()}/${vehicle.yearId}/?section=privacypolicy`;
    }

    // Determine panels to show
    if (requestedPanels.length !== 0) {
      let existingPanels = requestedPanels.filter((panel: string) => {
        if (panel === Panels.EXPERT_REVIEWS && (editorOverview && editorOverview.text && editorOverview.text.length > 0)) {
          return panel;
        }
        if (panel === Panels.EXPERT_RATINGS && (expertRating && expertRating.overallRating && expertRating.overallRating !== '0')) {
          return panel;
        }
        if (panel === Panels.CONSUMER_REVIEWS && numberOfReviews > 0) {
          return panel;
        }
        if (panel === Panels.CONSUMER_RATINGS && (consumerRating && consumerRating.overallRating && consumerRating.overallRating !== '0')) {
          return panel;
        }
        if (panel === Panels.AWARDS && (vehicleAward && vehicleAward.awards && vehicleAward.awards.length > 0)) {
          return panel;
        }
        return '';
      });

      // Filter panels that have data
      requestedPanels = requestedPanels.filter((panel: string) => {
        if (panel === Panels.EXPERT_REVIEWS) {
          return panel;
        }
        if (panel === Panels.EXPERT_RATINGS) {
          return panel;
        }
        if (panel === Panels.CONSUMER_REVIEWS) {
          return panel;
        }
        if (panel === Panels.CONSUMER_RATINGS) {
          return panel;
        }
        if (panel === Panels.AWARDS && (vehicleAward && vehicleAward.awards && vehicleAward.awards.length > 0)) {
          return panel;
        }
        return '';
      });

      // Get html component but if requested panels have no data, get error message
      if (existingPanels.length === 0) {
        panelsToDisplay = <GetErrorPanel />;
      } else {
        dataReceived((requestedPanels.map((panel: string) => `;editorial_${panel}`)).join());

        // If there is only 1 panel with data, open the accordion
        panelsToDisplay = requestedPanels.map((panel: string) => (
          <ValidPanel
            key={panel}
            panel={panel}
            isOpen={requestedPanels.length === 1}
            shouldEnableKBBLink={shouldEnableKBBLink}
          />
        ));
      }
    }
  }

  if(fixedheight){
    return (
      <Page id="WidgetPage" style={fixedHeightStyle}>
        <StickyHeaderContainer>
          <Header />
          <FlexContainer>
            <FlexRow>
              <FlexCell>
                <VehicleInfo requestedPanels={requestedPanels} isOptedOutOfEWBacklink={isOptedOutOfEWBacklink}/>
              </FlexCell>
            </FlexRow>
          </FlexContainer>
        </StickyHeaderContainer>
        <PageContent>
          <FlexContainer>
            {panelsToDisplay}
            <FlexRow>
              <FlexCell>
                <Footer
                  readMoreLink={readMoreLink}
                  privacyPolicyLink={privacyPolicyLink}
                />
              </FlexCell>
            </FlexRow>
          </FlexContainer>
        </PageContent>
      </Page>
    );
  }

  return (
    <Page id="WidgetPage" style={resizableHeightStyle} ref={contentArea}>
      <StickyHeaderContainer>
        <Header />
        <FlexContainer>
          <FlexRow>
            <FlexCell>
              <VehicleInfo requestedPanels={requestedPanels} isOptedOutOfEWBacklink={isOptedOutOfEWBacklink}/>
            </FlexCell>
          </FlexRow>
        </FlexContainer>
      </StickyHeaderContainer>
      <ResizablePageContent>
        <FlexContainer>
          {panelsToDisplay}
          <FlexRow>
            <FlexCell>
              <Footer
                readMoreLink={readMoreLink}
                privacyPolicyLink={privacyPolicyLink}
              />
            </FlexCell>
          </FlexRow>
        </FlexContainer>
      </ResizablePageContent>
    </Page>
  );


};

export default SewWidget;
